import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatServicePrice } from '../services/currencyUtils';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  Avatar,
  Chip,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Link as MuiLink,
  Autocomplete
} from '@mui/material';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { normalizeImageUrl } from '../services/imageUtils';
import { locationApi } from '../services/locationApi';

const TabPanel = ({ children, value, index }) => (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
      {value === index && children}
    </Box>
);

const formatTimestamp = (ts) => (ts ? new Date(ts).toLocaleString() : 'N/A');

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { currentUser } = useAuth();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const [tab, setTab] = useState(0);

  // Posts
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState('');
  const [postsPage, setPostsPage] = useState(0);
  const [postsRowsPerPage, setPostsRowsPerPage] = useState(20);
  const [postsTotal, setPostsTotal] = useState(0);

  // Comments
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState('');
  const [commentsPage, setCommentsPage] = useState(0);
  const [commentsRowsPerPage, setCommentsRowsPerPage] = useState(20);
  const [commentsTotal, setCommentsTotal] = useState(0);

  // Notes
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState('');
  const [notesPage, setNotesPage] = useState(0);
  const [notesRowsPerPage, setNotesRowsPerPage] = useState(20);
  const [notesTotal, setNotesTotal] = useState(0);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null); // { id, note }
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // City Manager
  const [cmCities, setCmCities] = useState([]);          // assigned CityManagerDto[]
  const [cmLoading, setCmLoading] = useState(false);
  const [cmError, setCmError] = useState('');

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const userData = await api.user.getUserById(id);
      setUser(userData);
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError('Failed to load user details. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [id, api.user]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const fetchPosts = useCallback(async () => {
    try {
      setPostsLoading(true);
      setPostsError('');
      const data = await api.posts.getPostsByUserAdmin(id, { page: postsPage, size: postsRowsPerPage });
      setPosts(data?.content || []);
      setPostsTotal(data?.totalElements || 0);
    } catch (err) {
      console.error('Error fetching user posts:', err);
      setPostsError('Failed to load posts.');
    } finally {
      setPostsLoading(false);
    }
  }, [api.posts, id, postsPage, postsRowsPerPage]);

  const fetchComments = useCallback(async () => {
    try {
      setCommentsLoading(true);
      setCommentsError('');
      const data = await api.posts.getCommentsByUserAdmin(id, { page: commentsPage, size: commentsRowsPerPage });
      setComments(data?.content || []);
      setCommentsTotal(data?.totalElements || 0);
    } catch (err) {
      console.error('Error fetching user comments:', err);
      setCommentsError('Failed to load comments.');
    } finally {
      setCommentsLoading(false);
    }
  }, [api.posts, id, commentsPage, commentsRowsPerPage]);

  const fetchNotes = useCallback(async () => {
    try {
      setNotesLoading(true);
      setNotesError('');
      const data = await api.adminUserNotes.list(id, { page: notesPage, size: notesRowsPerPage });
      setNotes(data?.content || []);
      setNotesTotal(data?.totalElements || 0);
    } catch (err) {
      console.error('Error fetching admin notes:', err);
      setNotesError('Failed to load notes.');
    } finally {
      setNotesLoading(false);
    }
  }, [api.adminUserNotes, id, notesPage, notesRowsPerPage]);

  const fetchCityManagerData = useCallback(async () => {
    setCmLoading(true);
    setCmError('');
    try {
      const data = await api.cityManager.getManagedCities(id);
      setCmCities(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching city manager assignments:', err);
      setCmError('Failed to load city manager assignments.');
    } finally {
      setCmLoading(false);
    }
  }, [api.cityManager, id]);

  useEffect(() => {
    if (tab === 1) fetchPosts();
  }, [tab, fetchPosts]);

  useEffect(() => {
    if (tab === 2) fetchComments();
  }, [tab, fetchComments]);

  useEffect(() => {
    if (tab === 3) fetchNotes();
  }, [tab, fetchNotes]);

  useEffect(() => {
    if (tab === 4) fetchCityManagerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, fetchCityManagerData]);

  const handleBack = () => {
    navigate('/users');
  };

  const handleToggleSuspend = async () => {
    if (!user) return;
    setActionError('');
    setActionMessage('');
    try {
      if (user.suspended) {
        await api.user.unsuspend(user.id);
        setUser({ ...user, suspended: false });
        setActionMessage(`User ${user.email} has been unsuspended.`);
      } else {
        await api.user.suspend(user.id);
        setUser({ ...user, suspended: true });
        setActionMessage(`User ${user.email} has been suspended.`);
      }
    } catch (err) {
      console.error('Error toggling suspension:', err);
      setActionError('Failed to update user status.');
    }
    setTimeout(() => setActionMessage(''), 3000);
  };

  const handleDeleteUser = async () => {
    if (!user) return;
    setDeleting(true);
    setActionError('');
    try {
      await api.user.deleteUser(user.id);
      setDeleteDialogOpen(false);
      navigate('/users');
    } catch (err) {
      console.error('Error deleting user:', err);
      setActionError('Failed to delete user. Please try again.');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateNote = async () => {
    const text = newNote.trim();
    if (!text) return;
    try {
      setSavingNote(true);
      await api.adminUserNotes.create(id, text);
      setNewNote('');
      // Reset to first page so the new note is visible at the top.
      if (notesPage !== 0) {
        setNotesPage(0);
      } else {
        await fetchNotes();
      }
    } catch (err) {
      console.error('Error creating note:', err);
      setNotesError('Failed to save note.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleSaveEditedNote = async () => {
    if (!editingNote) return;
    const text = (editingNote.note || '').trim();
    if (!text) return;
    try {
      setSavingNote(true);
      await api.adminUserNotes.update(editingNote.id, text);
      setEditingNote(null);
      await fetchNotes();
    } catch (err) {
      console.error('Error updating note:', err);
      setNotesError('Failed to update note.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleCmAssignCity = async (city) => {
    setCmError('');
    try {
      await api.cityManager.assignCity(city.id, Number(id));
      await fetchCityManagerData();
    } catch (err) {
      console.error('Error assigning city:', err);
      setCmError('Failed to assign city.');
    }
  };

  const handleCmUnassignCity = async (cityManagerDto) => {
    setCmError('');
    try {
      await api.cityManager.unassignCity(cityManagerDto.cityId, Number(id));
      await fetchCityManagerData();
    } catch (err) {
      console.error('Error unassigning city:', err);
      setCmError('Failed to remove city assignment.');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note? This action cannot be undone.')) return;
    try {
      await api.adminUserNotes.delete(noteId);
      await fetchNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      setNotesError('Failed to delete note.');
    }
  };

  if (loading) {
    return (
        <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
    );
  }

  if (error) {
    return (
        <Container sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>Dashboard</Button>
            <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={handleBack}>Back to Users</Button>
          </Stack>
        </Container>
    );
  }

  if (!user) {
    return (
        <Container sx={{ mt: 4 }}>
          <Alert severity="warning">User not found</Alert>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>Dashboard</Button>
            <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={handleBack}>Back to Users</Button>
          </Stack>
        </Container>
    );
  }

  const isSelf = user.email && currentUser?.email && user.email === currentUser.email;

  return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>Dashboard</Button>
          <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={handleBack}>Back to Users</Button>
        </Stack>

        <Typography variant="h4" component="h1" gutterBottom>
          User Details
        </Typography>

        {actionMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>{actionMessage}</Alert>
        )}
        {actionError && (
            <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>
        )}

        {/* Header card with admin actions */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
              {user.picture ? (
                  <Avatar
                      src={normalizeImageUrl(user.picture)}
                      alt={user.name}
                      sx={{ width: 150, height: 150 }}
                  />
              ) : (
                  <Avatar sx={{ width: 150, height: 150, fontSize: '3rem' }}>
                    {user.name.charAt(0).toUpperCase()}
                  </Avatar>
              )}
            </Grid>
            <Grid item xs={12} md={9}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h5" component="h2">
                  {user.name}
                </Typography>
                {user.suspended && (
                    <Chip label="Suspended" color="error" size="small" />
                )}
              </Stack>
              <Typography variant="body1" gutterBottom>
                <strong>Email:</strong> {user.email}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Email Verified:</strong> {user.emailVerified ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>User State:</strong> {user.userState || 'N/A'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Auth ID:</strong> {user.authId || 'N/A'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Created:</strong> {formatTimestamp(user.createdAt)}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Last Updated:</strong> {formatTimestamp(user.updatedAt)}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Phone:</strong> {user.phone || 'N/A'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Address:</strong> {user.address || 'N/A'}
              </Typography>

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                    control={
                      <Switch
                          checked={!user.suspended}
                          onChange={handleToggleSuspend}
                          color="primary"
                          disabled={isSelf}
                      />
                    }
                    label={user.suspended ? 'Suspended (toggle to unsuspend)' : 'Active (toggle to suspend)'}
                />
                {isSelf && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      You cannot suspend your own account.
                    </Typography>
                )}
              </Box>

              <Box sx={{ mt: 2 }}>
                <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={isSelf}
                >
                  Delete account
                </Button>
                {isSelf && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      You cannot delete your own account.
                    </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="Profile" />
            <Tab label={`Posts${postsTotal ? ` (${postsTotal})` : ''}`} />
            <Tab label={`Comments${commentsTotal ? ` (${commentsTotal})` : ''}`} />
            <Tab label={`Admin Notes${notesTotal ? ` (${notesTotal})` : ''}`} />
            <Tab label={`City Manager${cmCities.length ? ` (${cmCities.length})` : ''}`} />
          </Tabs>

          <Box sx={{ p: 2 }}>
            <TabPanel value={tab} index={0}>
              <ProfilePanel user={user} />
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <PostsPanel
                  loading={postsLoading}
                  error={postsError}
                  posts={posts}
                  page={postsPage}
                  rowsPerPage={postsRowsPerPage}
                  total={postsTotal}
                  onPageChange={(_, p) => setPostsPage(p)}
                  onRowsPerPageChange={(e) => {
                    setPostsRowsPerPage(parseInt(e.target.value, 10));
                    setPostsPage(0);
                  }}
              />
            </TabPanel>

            <TabPanel value={tab} index={2}>
              <CommentsPanel
                  loading={commentsLoading}
                  error={commentsError}
                  comments={comments}
                  page={commentsPage}
                  rowsPerPage={commentsRowsPerPage}
                  total={commentsTotal}
                  onPageChange={(_, p) => setCommentsPage(p)}
                  onRowsPerPageChange={(e) => {
                    setCommentsRowsPerPage(parseInt(e.target.value, 10));
                    setCommentsPage(0);
                  }}
              />
            </TabPanel>

            <TabPanel value={tab} index={3}>
              <NotesPanel
                  loading={notesLoading}
                  error={notesError}
                  notes={notes}
                  page={notesPage}
                  rowsPerPage={notesRowsPerPage}
                  total={notesTotal}
                  newNote={newNote}
                  setNewNote={setNewNote}
                  saving={savingNote}
                  onCreate={handleCreateNote}
                  onEdit={(note) => setEditingNote({ id: note.id, note: note.note })}
                  onDelete={handleDeleteNote}
                  onPageChange={(_, p) => setNotesPage(p)}
                  onRowsPerPageChange={(e) => {
                    setNotesRowsPerPage(parseInt(e.target.value, 10));
                    setNotesPage(0);
                  }}
              />
            </TabPanel>

            <TabPanel value={tab} index={4}>
              <CityManagerPanel
                  loading={cmLoading}
                  error={cmError}
                  assignedCities={cmCities}
                  onAssign={handleCmAssignCity}
                  onUnassign={handleCmUnassignCity}
              />
            </TabPanel>
          </Box>
        </Paper>

        {/* Delete account confirmation dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs">
          <DialogTitle>Delete account</DialogTitle>
          <DialogContent>
            <Typography>
              This will <strong>anonymize</strong> <strong>{user?.name}</strong> ({user?.email}) — their
              PII will be wiped and their account marked as Deleted. Posts and comments are
              kept but detached from personal data. This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
                variant="contained"
                color="error"
                onClick={handleDeleteUser}
                disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete account'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit note dialog */}
        <Dialog open={!!editingNote} onClose={() => setEditingNote(null)} fullWidth maxWidth="sm">
          <DialogTitle>Edit note</DialogTitle>
          <DialogContent>
            <TextField
                fullWidth
                multiline
                minRows={4}
                value={editingNote?.note || ''}
                onChange={(e) => setEditingNote({ ...editingNote, note: e.target.value })}
                sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingNote(null)}>Cancel</Button>
            <Button
                variant="contained"
                onClick={handleSaveEditedNote}
                disabled={savingNote || !(editingNote?.note || '').trim()}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
  );
};

const ProfilePanel = ({ user }) => (
    <Box>
      {user.dogOwnerProfile && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                Dog Owner Profile
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Country:</strong> {user.dogOwnerProfile.country || 'N/A'}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>City:</strong> {user.dogOwnerProfile.city || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Created:</strong> {formatTimestamp(user.dogOwnerProfile.createdAt)}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Last Updated:</strong> {formatTimestamp(user.dogOwnerProfile.updatedAt)}
                  </Typography>
                </Grid>
              </Grid>

              {user.dogOwnerProfile.dogs && user.dogOwnerProfile.dogs.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      Dogs
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {user.dogOwnerProfile.dogs.map((dog, index) => (
                        <Card key={dog.id || index} sx={{ mb: 2, p: 2 }}>
                          <Grid container spacing={2}>
                            {dog.image && (
                                <Grid item xs={12} md={3}>
                                  <Box
                                      component="img"
                                      src={normalizeImageUrl(dog.image)}
                                      alt={dog.name}
                                      sx={{
                                        width: '100%',
                                        height: 'auto',
                                        borderRadius: 1,
                                        maxHeight: 200,
                                        objectFit: 'cover'
                                      }}
                                  />
                                </Grid>
                            )}
                            <Grid item xs={12} md={dog.image ? 9 : 12}>
                              <Typography variant="h6" component="h4">{dog.name}</Typography>
                              <Grid container spacing={1} sx={{ mt: 1 }}>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>Breed:</strong> {dog.breed?.nameEn || 'N/A'}
                                  </Typography>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>Age:</strong> {dog.age ?? 'N/A'}
                                  </Typography>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>Weight:</strong> {dog.weight !== null && dog.weight !== undefined ? `${dog.weight} kg` : 'N/A'}
                                  </Typography>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>Gender:</strong> {dog.gender || 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>Size:</strong> {dog.size?.nameEn || 'N/A'}
                                  </Typography>
                                  <Typography variant="body2" gutterBottom>
                                    <strong>Energy:</strong> {dog.energy?.nameEn || 'N/A'}
                                  </Typography>
                                </Grid>
                                {dog.about && (
                                    <Grid item xs={12}>
                                      <Typography variant="body2" gutterBottom>
                                        <strong>About:</strong> {dog.about}
                                      </Typography>
                                    </Grid>
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Card>
                    ))}
                  </Box>
              )}

              {user.dogOwnerProfile.followedGardens && user.dogOwnerProfile.followedGardens.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      Followed Gardens
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      {user.dogOwnerProfile.followedGardens.map((garden, index) => (
                          <Grid item xs={12} sm={6} md={4} key={garden.id || index}>
                            <Card sx={{ height: '100%' }}>
                              <CardContent>
                                <Typography variant="h6" component="h4" gutterBottom>
                                  {garden.gardenName || 'Unknown Garden'}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                  <strong>ID:</strong> {garden.id || 'N/A'}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                  <strong>City:</strong> {garden.city || 'N/A'}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Address:</strong> {garden.address || 'N/A'}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Rating:</strong> {garden.rating ?? 'N/A'}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                      ))}
                    </Grid>
                  </Box>
              )}
            </CardContent>
          </Card>
      )}

      {user.provider && (
          <Card>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                Provider Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>ID:</strong> {user.provider.id || 'N/A'}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Title:</strong> {user.provider.title || 'N/A'}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>About:</strong> {user.provider.about || 'N/A'}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Provider State:</strong> {user.provider.providerState || 'N/A'}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Instagram User:</strong> {user.provider.instagramUser || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Document URL:</strong> {user.provider.documentUrl ? normalizeImageUrl(user.provider.documentUrl) : 'N/A'}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Created:</strong> {formatTimestamp(user.provider.createdAt)}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Last Updated:</strong> {formatTimestamp(user.provider.updatedAt)}
                  </Typography>
                </Grid>
              </Grid>

              {user.provider.services && user.provider.services.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      Services
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      {user.provider.services.map((service, index) => (
                          <Grid item xs={12} sm={6} md={4} key={service.id || index}>
                            <Card sx={{ height: '100%' }}>
                              <CardContent>
                                <Typography variant="h6" component="h4" gutterBottom>
                                  {service.name || 'Unknown Service'}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Description:</strong> {service.description || 'N/A'}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Price:</strong> {service.price !== null && service.price !== undefined ? formatServicePrice(service, user.country) : 'N/A'}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                      ))}
                    </Grid>
                  </Box>
              )}

              {user.provider.reviews && user.provider.reviews.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      Reviews
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {user.provider.reviews.map((review, index) => (
                        <Card key={review.id || index} sx={{ mb: 2, p: 2 }}>
                          <Typography variant="body2" gutterBottom>
                            <strong>Rating:</strong> {review.rating ?? 'N/A'}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>Comment:</strong> {review.comment || 'N/A'}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>Date:</strong> {formatTimestamp(review.createdAt)}
                          </Typography>
                        </Card>
                    ))}
                  </Box>
              )}

              {user.provider.images && user.provider.images.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      Images
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      {user.provider.images.map((image, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Box
                                component="img"
                                src={normalizeImageUrl(image)}
                                alt={`Provider image ${index + 1}`}
                                sx={{
                                  width: '100%',
                                  height: 'auto',
                                  borderRadius: 1,
                                  maxHeight: 200,
                                  objectFit: 'cover'
                                }}
                            />
                          </Grid>
                      ))}
                    </Grid>
                  </Box>
              )}
            </CardContent>
          </Card>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            Legal Consents
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {[
              {
                label: 'Terms of Service',
                accepted: user.hasAcceptedTerms,
                acceptedAt: user.termsAcceptedAt,
                version: user.termsVersion,
              },
              {
                label: 'EULA',
                accepted: user.hasAcceptedEula,
                acceptedAt: user.eulaAcceptedAt,
                version: user.eulaVersion,
              },
              {
                label: 'Privacy Policy',
                accepted: user.hasAcceptedPrivacy,
                acceptedAt: user.privacyAcceptedAt,
                version: user.privacyVersion,
              },
            ].map(({ label, accepted, acceptedAt, version }) => (
              <Grid item xs={12} sm={4} key={label}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Chip
                    label={accepted ? 'Accepted' : 'Not accepted'}
                    color={accepted ? 'success' : 'default'}
                    size="small"
                  />
                  <Typography variant="body2"><strong>{label}</strong></Typography>
                </Box>
                {version && (
                  <Typography variant="body2" color="text.secondary">
                    Version: {version}
                  </Typography>
                )}
                {acceptedAt && (
                  <Typography variant="body2" color="text.secondary">
                    {formatTimestamp(acceptedAt)}
                  </Typography>
                )}
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {!user.dogOwnerProfile && !user.provider && (
          <Typography variant="body2" color="text.secondary">
            No additional profile information.
          </Typography>
      )}
    </Box>
);

const PostsPanel = ({ loading, error, posts, page, rowsPerPage, total, onPageChange, onRowsPerPageChange }) => {
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!posts.length) return <Typography variant="body2" color="text.secondary">No posts.</Typography>;

  return (
      <Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell>Visible</TableCell>
                <TableCell>Text</TableCell>
                <TableCell>Garden</TableCell>
                <TableCell>Likes</TableCell>
                <TableCell>Comments</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.map((post) => (
                  <TableRow key={post.id} hover>
                    <TableCell>
                      <MuiLink component={RouterLink} to={`/posts/${post.id}`}>
                        {post.id}
                      </MuiLink>
                    </TableCell>
                    <TableCell>{post.postType}</TableCell>
                    <TableCell>{post.audience}</TableCell>
                    <TableCell>{post.visible ? 'Yes' : 'No'}</TableCell>
                    <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {post.text || <em>(no text)</em>}
                    </TableCell>
                    <TableCell>{post.garden?.gardenName || post.garden?.id || '—'}</TableCell>
                    <TableCell>{post.totalEmotions ?? 0}</TableCell>
                    <TableCell>{post.totalComments ?? 0}</TableCell>
                    <TableCell>{formatTimestamp(post.createdTimestamp)}</TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
        />
      </Box>
  );
};

const CommentsPanel = ({ loading, error, comments, page, rowsPerPage, total, onPageChange, onRowsPerPageChange }) => {
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!comments.length) return <Typography variant="body2" color="text.secondary">No comments.</Typography>;

  return (
      <Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Post</TableCell>
                <TableCell>Comment</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comments.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>{c.id}</TableCell>
                    <TableCell>
                      <MuiLink component={RouterLink} to={`/posts/${c.postId}`}>
                        #{c.postId}
                      </MuiLink>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 480 }}>{c.text}</TableCell>
                    <TableCell>{formatTimestamp(c.createdTimestamp)}</TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
        />
      </Box>
  );
};

const NotesPanel = ({
                      loading, error, notes, page, rowsPerPage, total,
                      newNote, setNewNote, saving, onCreate, onEdit, onDelete,
                      onPageChange, onRowsPerPageChange
                    }) => (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Add a note
        </Typography>
        <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="e.g. Suspended on 2026-05-10 — repeated harassment reports."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
        />
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
              variant="contained"
              onClick={onCreate}
              disabled={saving || !newNote.trim()}
          >
            Save note
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : !notes.length ? (
          <Typography variant="body2" color="text.secondary">No notes yet.</Typography>
      ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Note</TableCell>
                    <TableCell>Author</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notes.map((n) => (
                      <TableRow key={n.id} hover>
                        <TableCell sx={{ maxWidth: 520, whiteSpace: 'pre-wrap' }}>{n.note}</TableCell>
                        <TableCell>
                          {n.authorName || n.authorEmail || (n.authorId ? `#${n.authorId}` : 'Unknown')}
                        </TableCell>
                        <TableCell>{formatTimestamp(n.createdAt)}</TableCell>
                        <TableCell>{formatTimestamp(n.updatedAt)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => onEdit(n)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => onDelete(n.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 20, 50]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />
          </>
      )}
    </Box>
);

const CityManagerPanel = ({ loading, error, assignedCities, onAssign, onUnassign }) => {
  // Build a set of already-assigned cityIds for fast lookup
  const assignedCityIds = new Set(assignedCities.map((c) => c.cityId));

  // Server-side search so the FULL city catalog is searchable (the list is large;
  // a preloaded page would silently omit most cities). Debounced query to the
  // admin cities endpoint as the admin types.
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchLoading(true);
      locationApi
        .adminListCities({ q: inputValue, size: 50 })
        .then((data) => {
          const content = Array.isArray(data?.content) ? data.content : [];
          setOptions(content.filter((c) => !assignedCityIds.has(c.id)));
        })
        .catch(() => setOptions([]))
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, assignedCities]);

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Assigned Cities
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Assigning the first city grants the <strong>city_manager</strong> role. Removing the last city revokes it automatically.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {assignedCities.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No cities assigned. Use the picker below to assign one.
            </Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 3 }}>
              {assignedCities.map((cm) => (
                <Chip
                  key={cm.id}
                  label={cm.cityNameEn || cm.cityNameHe || `City #${cm.cityId}`}
                  onDelete={() => onUnassign(cm)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}

          <Autocomplete
            options={options}
            value={null}
            inputValue={inputValue}
            onInputChange={(_, val, reason) => {
              if (reason === 'input') setInputValue(val);
            }}
            onChange={(_, city) => {
              if (city) {
                onAssign(city);
                setInputValue('');
              }
            }}
            getOptionLabel={(c) =>
              c ? `${c.nameEn || ''}${c.nameHe ? ` / ${c.nameHe}` : ''} (#${c.id})` : ''
            }
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            filterOptions={(x) => x}
            loading={searchLoading}
            loadingText="Searching…"
            noOptionsText="Type to search cities"
            blurOnSelect
            clearOnBlur
            sx={{ maxWidth: 480 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add city"
                placeholder="Search the full city list to assign"
                helperText="Type to search; selecting a city immediately assigns it."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </>
      )}
    </Box>
  );
};

export default UserDetails;
